import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardActions,
  Avatar,
  Typography, 
  Skeleton,
  List,
  ListItem,
  ListItemText,
  Button,
  Box,
  CircularProgress
} from '@mui/material';
import { SvgIconComponent } from '@mui/icons-material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link } from 'react-router-dom';
import { IReuniao, StatusReuniaoEnum } from '../../commons/types/crm';
import { useTranslation } from '../../commons/useTranslasion';
import { useDateFormatters } from '../../utils/useDateFormatters';
import { themeConstants } from '../../commons/theme';

// Componente para exibir mensagem quando não há dados
const EmptyState = ({ message }: { message: string }) => (
  <Box sx={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    py: 2
  }}>
    <Typography variant="body1">{message}</Typography>
  </Box>
);

// Componente para estado de carregamento usando Skeleton
const ReunioesSkeleton = () => (
  <Box sx={{ px: 1 }}>
    <Skeleton variant="rectangular" height={30} sx={{ mb: 1 }} />
    <Skeleton variant="rectangular" height={30} sx={{ mb: 1 }} />
    <Skeleton variant="rectangular" height={30} /> 
  </Box>
);

interface CardReuniaoProps {
  Icon: SvgIconComponent;
  titulo: string;
  reunioesCard: IReuniao[];
  loading: boolean;
}

export const CardReuniao: React.FC<CardReuniaoProps> = ({ 
  Icon, 
  titulo, 
  reunioesCard,
  loading 
}) => {
  const { translate } = useTranslation();
  const { formatarDiaMes, formatarHora } = useDateFormatters();
  const { colors } = themeConstants;

  // Função para formatar data e hora da reunião
  const formatDateTime = (reuniao: IReuniao) => {
    const isRealizada = 
      reuniao.status === StatusReuniaoEnum.PENDENTE_APROVACAO || 
      reuniao.status === StatusReuniaoEnum.APROVADA;
    
    // Formata apenas dia/mês baseado no idioma
    const data = isRealizada 
      ? formatarDiaMes(reuniao.dtRealizadaInicio)
      : formatarDiaMes(reuniao.dtPrevista);
    
    // Formata a hora
    const hora = formatarHora(reuniao.dtRealizadaInicio);
    
    return `${data} ${hora}`;
  };

  return (
    <Card 
      elevation={2} 
      sx={{ 
        width: '100%', 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      <CardHeader
        avatar={
          <Avatar 
            sx={{ 
              bgcolor: 'primary.main',
              color: '#fff'
            }}
          >
            <Icon />
          </Avatar>
        }
        title={
          <Typography variant="subtitle1">{titulo}</Typography>
        }
      />
      
      <CardContent sx={{ 
        flex: '1 0 auto', 
        p: 2, 
        pt: 0,
        minHeight: '138px'
      }}>
        {loading ? (
          <ReunioesSkeleton />
        ) : (
          <List disablePadding>
            {reunioesCard?.length === 0 ? (
              <EmptyState message={translate('nenhumDadoASerExibido')} />
            ) : (
              reunioesCard.slice(0, 3).map((reuniao, index) => (
                <ListItem
                  key={index}
                  divider={index < Math.min(reunioesCard.length, 3) - 1}
                  secondaryAction={
                    <Typography variant="body2" color="text.secondary">
                      {formatDateTime(reuniao)}
                    </Typography>
                  }
                  sx={{ px: 1 }}
                >
                  <ListItemText 
                    primary={reuniao.nomeAssessorado.split(' ')[0]}
                    primaryTypographyProps={{
                      variant: 'body1',
                      fontWeight: 500
                    }}
                  />
                </ListItem>
              ))
            )}
          </List>
        )}
      </CardContent>
      
      <CardActions>
        <Button
          size="small"
          component={Link}
          to="/reunioes"
          endIcon={<ArrowForwardIcon />}
          sx={{ color: colors.blue.light }}
        >
          {translate('verMais')}
        </Button>
      </CardActions>
    </Card>
  );
};
